import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Buildings, UserFocus, Plus, Trash, UploadSimple } from "@phosphor-icons/react";

const empty = {
  role: "guard", name: "", first_name: "", last_name: "", email: "", password: "", password_repeat: "",
  phone: "", street: "", house_number: "", postal_code: "", city: "", country: "Nederland",
  company_name: "", kvk_number: "", birth_date: "", gender: "",
};

export default function Register() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [passes, setPasses] = useState([]); // [{ file, color }]
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const isGuard = form.role === "guard";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addPass = () => setPasses([...passes, { file: null, color: "blue" }]);
  const setPass = (i, patch) => setPasses(passes.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const removePass = (i) => setPasses(passes.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    if (form.password !== form.password_repeat) {
      setError(t("password_mismatch"));
      return;
    }
    setLoading(true);
    try {
      const name = isGuard ? `${form.first_name} ${form.last_name}`.trim() : form.name;
      const payload = {
        role: form.role, name, email: form.email, password: form.password, phone: form.phone,
        street: form.street, house_number: form.house_number, postal_code: form.postal_code,
        city: form.city, country: form.country, company_name: form.company_name,
      };
      if (isGuard) {
        Object.assign(payload, {
          first_name: form.first_name, last_name: form.last_name, kvk_number: form.kvk_number,
          birth_date: form.birth_date, gender: form.gender,
        });
      }
      const { data } = await api.post("/auth/register", payload);
      if (data.token) {
        if (isGuard) {
          for (const p of passes) {
            if (!p.file) continue;
            const fd = new FormData();
            fd.append("file", p.file);
            await api.post(`/upload?kind=security_pass&label=${encodeURIComponent(p.color)}`, fd);
          }
        }
        login(data);
        navigate("/dashboard");
      } else {
        setInfo(t("reg_neutral"));
      }
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const RoleBtn = ({ value, icon, label }) => (
    <button type="button" data-testid={`role-${value}`} onClick={() => setForm({ ...form, role: value })}
      className={`flex-1 flex flex-col items-center gap-2 border rounded-md p-4 transition-all ${form.role === value ? "border-[#E11D2A] bg-[#E11D2A]/10 ring-2 ring-[#E11D2A] text-white" : "border-white/15 text-zinc-300 hover:border-white/30"}`}>
      {icon}<span className="text-sm font-semibold">{label}</span>
    </button>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-12 bg-[#0A0A0A]">
      <div className="w-full max-w-lg bg-[#12161d] border border-white/10 rounded-2xl shadow-2xl p-8 animate-fade-up">
        <div className="flex justify-center mb-5"><img src="/guardlink-logo.jpg" alt="GuardLink" className="h-16 w-auto rounded-xl" /></div>
        <h1 className="font-head text-2xl font-bold tracking-tight text-white text-center">{t("register_title")}</h1>
        {error && <div data-testid="register-error" className="mt-4 text-sm bg-red-50 text-red-700 border border-red-200 rounded-md px-3 py-2">{error}</div>}
        {info && <div data-testid="register-info" className="mt-4 text-sm bg-[#E11D2A]/5 text-[#E11D2A] border border-[#E11D2A]/30 rounded-md px-3 py-2">{info} <Link to="/login" className="font-semibold underline">{t("login_title")}</Link></div>}

        <form onSubmit={submit} className="mt-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">{t("role")}</label>
            <div className="flex gap-3">
              <RoleBtn value="guard" icon={<UserFocus size={26} weight="duotone" className="text-[#E11D2A]" />} label={t("role_guard")} />
              <RoleBtn value="company" icon={<Buildings size={26} weight="duotone" className="text-[#E11D2A]" />} label={t("role_company")} />
            </div>
          </div>

          {/* Persoonsgegevens */}
          <Section title={isGuard ? t("section_personal") : t("section_company")} />
          {isGuard ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("first_name")}><input data-testid="reg-first-name" required value={form.first_name} onChange={set("first_name")} className="inp" /></Field>
                <Field label={t("last_name")}><input data-testid="reg-last-name" required value={form.last_name} onChange={set("last_name")} className="inp" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("birth_date")}><input data-testid="reg-birth-date" type="date" required value={form.birth_date} onChange={set("birth_date")} className="inp" /></Field>
                <Field label={t("gender")}>
                  <select data-testid="reg-gender" required value={form.gender} onChange={set("gender")} className="inp">
                    <option value="" disabled>—</option>
                    <option value="male">{t("gender_male")}</option>
                    <option value="female">{t("gender_female")}</option>
                    <option value="other">{t("gender_other")}</option>
                  </select>
                </Field>
              </div>
            </>
          ) : (
            <Field label={t("contact_name")}><input data-testid="reg-name" required value={form.name} onChange={set("name")} className="inp" /></Field>
          )}

          {/* Bedrijfsgegevens */}
          <div className="grid grid-cols-2 gap-3">
            <Field label={isGuard ? t("zzp_company_name") : t("company_name")}><input data-testid="reg-company-name" required value={form.company_name} onChange={set("company_name")} className="inp" /></Field>
            {isGuard && <Field label={t("kvk")}><input data-testid="reg-kvk" required value={form.kvk_number} onChange={set("kvk_number")} className="inp" /></Field>}
          </div>

          {/* Account */}
          <Section title={t("section_account")} />
          <Field label={t("email")}><input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} className="inp" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("password")}><input data-testid="reg-password" type="password" required value={form.password} onChange={set("password")} className="inp" /></Field>
            <Field label={t("password_repeat")}><input data-testid="reg-password-repeat" type="password" required value={form.password_repeat} onChange={set("password_repeat")} className="inp" /></Field>
          </div>
          <Field label={t("phone")}><input data-testid="reg-phone" required value={form.phone} onChange={set("phone")} className="inp" /></Field>

          {/* Adres */}
          <Section title={t("section_address")} />
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Field label={t("street")}><input data-testid="reg-street" required value={form.street} onChange={set("street")} className="inp" /></Field></div>
            <Field label={t("house_number")}><input data-testid="reg-house-number" required value={form.house_number} onChange={set("house_number")} className="inp" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("postal_code")}><input data-testid="reg-postal-code" required value={form.postal_code} onChange={set("postal_code")} className="inp" /></Field>
            <Field label={t("city")}><input data-testid="reg-city" required value={form.city} onChange={set("city")} className="inp" /></Field>
            <Field label={t("country")}><input data-testid="reg-country" required value={form.country} onChange={set("country")} className="inp" /></Field>
          </div>

          {/* Beveiligingspassen (alleen zzp) */}
          {isGuard && (
            <>
              <Section title={t("section_passes")} />
              <p className="text-xs text-zinc-500 -mt-2">{t("passes_hint")}</p>
              {passes.map((p, i) => (
                <div key={i} data-testid={`pass-row-${i}`} className="border border-white/10 rounded-md p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <select data-testid={`pass-color-${i}`} value={p.color} onChange={(e) => setPass(i, { color: e.target.value })} className="inp flex-1">
                      <option value="blue">{t("pass_blue")}</option>
                      <option value="green">{t("pass_green")}</option>
                      <option value="grey">{t("pass_grey")}</option>
                      <option value="other">{t("pass_other")}</option>
                    </select>
                    <button type="button" data-testid={`pass-remove-${i}`} onClick={() => removePass(i)} className="p-2 text-zinc-500 hover:text-red-600 transition-colors"><Trash size={18} /></button>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-zinc-400 border border-dashed border-white/20 rounded-md px-3 py-2 cursor-pointer hover:border-[#E11D2A] transition-colors">
                    <UploadSimple size={18} />
                    <span className="truncate">{p.file ? p.file.name : t("choose_file")}</span>
                    <input data-testid={`pass-file-${i}`} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => setPass(i, { file: e.target.files[0] })} />
                  </label>
                </div>
              ))}
              <button type="button" data-testid="add-pass-btn" onClick={addPass} className="flex items-center gap-1.5 text-sm font-semibold text-[#E11D2A] hover:text-[#B0121F] transition-colors">
                <Plus size={16} /> {t("add_pass")}
              </button>
            </>
          )}

          <button data-testid="register-submit" disabled={loading} className="w-full bg-[#E11D2A] text-white hover:bg-[#B0121F] font-semibold rounded-md py-2.5 transition-colors disabled:opacity-60">{loading ? t("loading") : t("submit")}</button>
        </form>
        <p className="mt-5 text-sm text-zinc-400 text-center">{t("have_account")} <Link data-testid="to-login" to="/login" className="text-[#E11D2A] font-semibold hover:text-[#ff4d5a]">{t("login_title")}</Link></p>
      </div>
      <style>{`.inp{width:100%;background:#0A0A0A;border:1px solid rgba(255,255,255,.15);color:#fff;border-radius:.375rem;padding:.5rem .75rem;font-size:.875rem;outline:none}.inp:focus{box-shadow:0 0 0 2px #E11D2A}.inp::placeholder{color:#71717a}`}</style>
    </div>
  );
}

const Section = ({ title }) => (
  <div className="pt-1"><h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10 pb-1.5">{title}</h2></div>
);

const Field = ({ label, children }) => (
  <div><label className="block text-sm font-medium text-zinc-300 mb-1.5">{label}</label>{children}</div>
);
